import classnames from "classnames";
import { SingletonRouter, withRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, IntlShape, defineMessages } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getCbtToken } from "shared/data/customerTokens";
import { countPassengers } from "shared/lib/functions";
import { PassengersCount } from "shared/lib/golObjectTypes/PassengerTypes";
import {
  isPassengersSelectError,
  setPassengersSelectErrorMessage,
} from "shared/lib/passengersSelect/isPassengersSelectError";
import { formattedMessageParams } from "shared/messages";

import Img from "@components/FileServer/Img";
import PassengersSelect from "@components/SearchForm/PassengersSelect";

import { correctRoomWording } from "@lib/Hotels/correctRoomWording";
import { useOnClickOutside } from "@lib/hooks";
import {
  checkIsFlightSearchForm,
  checkIsHotelSearchForm,
} from "@lib/webFunctions";
import { setClosedHotelPropertyError } from "@shared/data/actions/search";
import { AppState } from "@shared/data/reducers";

const HEIGHT_FROM_TOP = 350;
const HEIGHT_FROM_BOTTOM = 340;
const EXPORT_HTML_PACKAGE = Boolean(process.env.NEXT_PUBLIC_EXPORT_PACKAGE);

const {
  PropertiesSelectOnePassenger,
  PropertiesSelectTwoThreePassengers,
  PropertiesSelectFourPlusPassengers,
  PropertiesSelectGuestsOne,
  PropertiesSelectGuestsFourPlus,
  PropertiesSelectClassEconomy,
  PropertiesSelectClassPremiumEconomy,
  PropertiesSelectClassBusiness,
  PropertiesSelectClassFirstClass,
} = defineMessages({
  PropertiesSelectOnePassenger: formattedMessageParams(
    "PropertiesSelect.OnePassenger"
  ),
  PropertiesSelectTwoThreePassengers: formattedMessageParams(
    "PropertiesSelect.TwoThreePassengers"
  ),
  PropertiesSelectFourPlusPassengers: formattedMessageParams(
    "PropertiesSelect.FourPlusPassengers"
  ),
  PropertiesSelectGuestsOne: formattedMessageParams("General.guestsCountOne"),

  PropertiesSelectGuestsFourPlus: formattedMessageParams(
    "General.guestsCountOther"
  ),
  PropertiesSelectClassEconomy: formattedMessageParams(
    "SearchForm.classEconomy"
  ),
  PropertiesSelectClassPremiumEconomy: formattedMessageParams(
    "SearchForm.classPremiumEconomy"
  ),
  PropertiesSelectClassBusiness: formattedMessageParams(
    "SearchForm.classBusiness"
  ),
  PropertiesSelectClassFirstClass: formattedMessageParams(
    "SearchForm.classFirstClass"
  ),
});

export function getFlightClasses(
  intl: IntlShape
): Array<{ value: string; label: string }> {
  return [
    {
      value: "ECO",
      label: intl.formatMessage(PropertiesSelectClassEconomy).toUpperCase(),
    },
    {
      value: "PRE",
      label: intl
        .formatMessage(PropertiesSelectClassPremiumEconomy)
        .toUpperCase(),
    },
    {
      value: "BUS",
      label: intl.formatMessage(PropertiesSelectClassBusiness).toUpperCase(),
    },
    {
      value: "1ST",
      label: intl.formatMessage(PropertiesSelectClassFirstClass).toUpperCase(),
    },
  ];
}

interface PropertiesSelectProps {
  intl: IntlShape;
  passengers: PassengersCount;
  rooms?: number;
  flightClass?: string;
  onChangeFlightClass?: (flightClass: string) => void;
  defaultFlightClassName?: string;
  onAddCounterHandler: (type: string) => void;
  onRemoveCounterHandler: (type: string) => void;
  router: SingletonRouter;
  isBestOfferSelect?: boolean;
  selectComponentPrefix: string;
  maxNumPassengers: number;
  resetPassengers?: () => void;
  showFlightClassInToggle?: boolean;
  centerPropertiesPanel?: boolean;
}

interface ErrorState {
  error: boolean;
  errorMessage: number | null;
}

const PropertiesSelect: React.FC<PropertiesSelectProps> = ({
  intl,
  passengers,
  flightClass,
  onChangeFlightClass,
  defaultFlightClassName,
  onAddCounterHandler: propOnAddCounterHandler,
  onRemoveCounterHandler: propOnRemoveCounterHandler,
  router,
  isBestOfferSelect = false,
  selectComponentPrefix,
  maxNumPassengers,
  resetPassengers,
  showFlightClassInToggle = true,
  centerPropertiesPanel = false,
}) => {
  const [showProperties, setShowProperties] = useState<boolean>(
    EXPORT_HTML_PACKAGE ?? false
  );
  const [errorState, setErrorState] = useState<ErrorState>({
    error: false,
    errorMessage: null,
  });

  const [isOnTop, setIsOnTop] = useState<boolean>(false);

  const propertiesPanelRef = useRef<HTMLDivElement>(null);

  const getFocusablePropertiesElements = useCallback((): HTMLElement[] => {
    const propertiesPanel = document.getElementById(
      `${selectComponentPrefix}-properties`
    );

    if (!propertiesPanel) {
      return [];
    }

    return Array.from(
      propertiesPanel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (element) =>
        element.getAttribute("aria-hidden") !== "true" &&
        element.offsetParent !== null
    );
  }, [selectComponentPrefix]);

  const cbtToken = getCbtToken();
  const dispatch = useDispatch();
  const isCbtEnabled = cbtToken;

  const clearErrorMessage = (): void => {
    setErrorState({
      error: false,
      errorMessage: null,
    });
  };

  const setErrorMessage = (messageNumber: number): void => {
    if (messageNumber === null) {
      return clearErrorMessage();
    }

    setErrorState({
      error: true,
      errorMessage: messageNumber,
    });
  };

  const onAddCounterHandler = (type: string): void => {
    if (!isPassengersSelectError(passengers)) {
      clearErrorMessage();
    }
    propOnAddCounterHandler(type);
  };

  const onRemoveCounterHandler = (type: string): void => {
    if (!isPassengersSelectError(passengers)) {
      clearErrorMessage();
    }
    propOnRemoveCounterHandler(type);
  };

  useEffect(() => {
    const currentIsHotelSearchForm =
      !isBestOfferSelect && checkIsHotelSearchForm(router);

    if (
      resetPassengers &&
      currentIsHotelSearchForm &&
      !checkIsFlightSearchForm(router)
    ) {
      resetPassengers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBestOfferSelect, router]);

  const isVisible = (): void => {
    const el = document.querySelector(
      `#${selectComponentPrefix}-div_toggle_properties`
    );

    if (el) {
      const bounding = el.getBoundingClientRect();
      if (
        bounding.top < HEIGHT_FROM_TOP ||
        bounding.bottom + HEIGHT_FROM_BOTTOM <=
          (window.innerHeight || document.documentElement.clientHeight)
      ) {
        setIsOnTop(false);
      } else {
        setIsOnTop(true);
      }
    }
  };

  const { searchHotels, cbt, closedHotelPropertyError } = useSelector(
    (state: AppState) => ({
      searchHotels: state.search.searchHotels,
      cbt: state.cbt,
      closedHotelPropertyError:
        state.search.searchHotels.closedHotelPropertyError,
    })
  );

  const numPassengers = countPassengers(passengers);

  const currentIsHotelSearchForm =
    !isBestOfferSelect && checkIsHotelSearchForm(router);

  const getHotelCbtValidationError = useCallback((): string | null => {
    const roomEntries = Object.values(searchHotels.rooms || {}) as {
      guests?: number;
    }[];

    const isSomeRoomEmpty = roomEntries.some((room, roomIndex) => {
      const numAssignedPassengers =
        Object.values(
          cbt.passengersForm?.hotelRoomAssignments?.[roomIndex] || {}
        )?.length || 0;
      return (room.guests || 0) === 0 && numAssignedPassengers === 0;
    });

    if (!cbt.passengersForm.travelReason || !cbt.passengersForm?.costCenter) {
      return "SearchForm.cbtSelectAdditionalMandatoryInformation";
    }
    if (isSomeRoomEmpty) {
      return "SearchForm.oneOfRoomEmpty";
    }
    return null;
  }, [searchHotels, cbt]);

  const handleClickOutside = useCallback(() => {
    if (!showProperties) {
      return;
    }

    if (isCbtEnabled && closedHotelPropertyError) {
      return;
    }

    if (
      !isCbtEnabled &&
      isPassengersSelectError(passengers) !== null &&
      !currentIsHotelSearchForm
    ) {
      return;
    }

    if (isCbtEnabled && currentIsHotelSearchForm) {
      const error = getHotelCbtValidationError();
      dispatch(setClosedHotelPropertyError(error));
    }

    setShowProperties(false);
    setErrorState({
      error: false,
      errorMessage: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showProperties,
    isCbtEnabled,
    closedHotelPropertyError,
    passengers,
    isBestOfferSelect,
    router,
    currentIsHotelSearchForm,
    dispatch,
    getHotelCbtValidationError,
  ]);

  useOnClickOutside(propertiesPanelRef, handleClickOutside);

  useEffect(() => {
    if (!showProperties || !isBestOfferSelect) {
      return;
    }

    window.setTimeout(() => {
      getFocusablePropertiesElements()[0]?.focus();
    }, 0);
  }, [getFocusablePropertiesElements, isBestOfferSelect, showProperties]);

  const correctGuestWording = (num: number): string => {
    const passengersCount = num || countPassengers(passengers);

    let wordPassengers = intl.formatMessage(PropertiesSelectGuestsFourPlus);
    if (passengersCount === 0 || passengersCount > 4) {
      wordPassengers = intl.formatMessage(PropertiesSelectGuestsFourPlus);
    } else if (passengersCount === 1) {
      wordPassengers = intl.formatMessage(PropertiesSelectGuestsOne);
    }

    return wordPassengers;
  };

  const correctPassengerWording = (num: number): string => {
    const passengersCount = num || countPassengers(passengers);

    let wordPassengers = intl.formatMessage(PropertiesSelectTwoThreePassengers);
    if (passengersCount === 0 || passengersCount > 4) {
      wordPassengers = intl.formatMessage(PropertiesSelectFourPlusPassengers);
    } else if (passengersCount === 1) {
      wordPassengers = intl.formatMessage(PropertiesSelectOnePassenger);
    }

    return wordPassengers;
  };

  const flightClasses = getFlightClasses(intl);
  const valueFlightClass = flightClasses.find(
    (oFlightClass) => oFlightClass.value === flightClass
  );

  const toggleShowProperties = (): void => {
    if (isCbtEnabled && showProperties === true) {
      dispatch(setClosedHotelPropertyError(getHotelCbtValidationError()));
    }

    if (
      !isCbtEnabled &&
      setPassengersSelectErrorMessage(passengers, setErrorMessage) &&
      showProperties &&
      !isHotelSearchForm
    ) {
      return;
    }

    isVisible();
    setShowProperties(!showProperties);
    setErrorState({
      error: false,
      errorMessage: null,
    });
  };
  const handleTogglePropertiesKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    toggleShowProperties();
  };
  const handlePropertiesKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (!isBestOfferSelect || event.key !== "Tab") {
      return;
    }

    const focusableElements = getFocusablePropertiesElements();

    if (focusableElements.length === 0) {
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const flightClassTranslation = {
    ECO: {
      label: intl.formatMessage(PropertiesSelectClassEconomy).toUpperCase(),
    },
    PRE: {
      label: intl
        .formatMessage(PropertiesSelectClassPremiumEconomy)
        .toUpperCase(),
    },
    BUS: {
      label: intl.formatMessage(PropertiesSelectClassBusiness).toUpperCase(),
    },
    "1ST": {
      label: intl.formatMessage(PropertiesSelectClassFirstClass).toUpperCase(),
    },
  };

  const isHotelSearchForm =
    !isBestOfferSelect && checkIsHotelSearchForm(router);

  const calculateAllHotelPassengers = (): {
    totalRooms: number;
    totalPassengers: number;
  } => {
    interface Room {
      ADT: number;
      CHD: number;
      guests: number;
      childAges?: number[];
    }

    const roomEntries = Object.values(searchHotels.rooms || {}) as Room[];

    const totalRooms = roomEntries.length;

    const totalPassengers = roomEntries.reduce(
      (sum, room, roomIndex) =>
        sum +
        (room.ADT || 0) +
        (room.CHD || 0) +
        (room.guests || 0) +
        (Object.values(
          cbt.passengersForm?.hotelRoomAssignments?.[roomIndex] || {}
        )?.length || 0),
      0
    );

    return {
      totalRooms,
      // This is done because we cannot have double state setting in redux for cbt and non cbt
      totalPassengers:
        !isCbtEnabled && totalPassengers === 0 ? 1 : totalPassengers,
    };
  };

  return (
    <>
      {showProperties && <div className="loader-wrapper-mini" />}
      <div ref={propertiesPanelRef}>
        <div
          className={classnames("form-properties-wrapper", {
            hotels: isHotelSearchForm,
          })}
          onClick={() => toggleShowProperties()}
          onKeyDown={handleTogglePropertiesKeyDown}
          role="button"
          id={`${selectComponentPrefix}-div_toggle_properties`}
          aria-controls={`${selectComponentPrefix}-properties`}
          aria-expanded={showProperties}
          tabIndex={0}
        >
          <span
            className={`${selectComponentPrefix}-inner-field-value nowrap`}
            id={`${selectComponentPrefix}-properties-value`}
          >
            {isHotelSearchForm
              ? `${
                  calculateAllHotelPassengers().totalPassengers
                } ${correctGuestWording(
                  calculateAllHotelPassengers().totalPassengers
                )}, ${
                  calculateAllHotelPassengers().totalRooms
                } ${correctRoomWording(
                  calculateAllHotelPassengers().totalRooms,
                  intl
                )}`
              : `${numPassengers} ${correctPassengerWording(numPassengers)}`}
          </span>
          {!isHotelSearchForm && showFlightClassInToggle && (
            <span
              className={`${selectComponentPrefix}-inner-field-additional nowrap`}
            >
              {flightClass
                ? flightClassTranslation[flightClass]?.label
                : defaultFlightClassName?.toUpperCase()}
            </span>
          )}

          <Img
            src="/static/images/ico-arrow-down.svg"
            id={`${selectComponentPrefix}-properties-arrow`}
            style={{
              padding: "2px",
              marginLeft: "5px",
              transition: "all .4s ease",
              transform: showProperties ? "rotate(180deg)" : null,
            }}
          />
        </div>
        {showProperties && (
          <div
            id={`${selectComponentPrefix}-properties`}
            className={classnames(
              `${selectComponentPrefix}-properties iframe`,
              {
                [`${selectComponentPrefix}-properties-top`]:
                  isOnTop && !isHotelSearchForm,
                [`${selectComponentPrefix}-properties-centered`]: centerPropertiesPanel,
              },
              { "hotel-properties-select": isHotelSearchForm }
            )}
            aria-label={intl.formatMessage({ id: "SearchForm.options" })}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handlePropertiesKeyDown}
            role="button"
          >
            <PassengersSelect
              isHotelSearchForm={isHotelSearchForm}
              onAddCounterHandler={onAddCounterHandler}
              onChangeFlightClass={onChangeFlightClass}
              onRemoveCounterHandler={onRemoveCounterHandler}
              passengers={passengers}
              flightClasses={flightClasses}
              valueFlightClass={valueFlightClass}
              displayShowToggleProperties
              setErrorMessage={setErrorMessage}
              toggleShowProperties={toggleShowProperties}
              error={errorState.error}
              errorMessage={errorState.errorMessage}
              selectComponentPrefix={selectComponentPrefix}
              allowClassSelection={!isBestOfferSelect}
              defaultFlightClassName={defaultFlightClassName}
              numPassengers={numPassengers}
              maxNumPassengers={maxNumPassengers}
            />
          </div>
        )}
        {closedHotelPropertyError && isHotelSearchForm && (
          <div>
            <div style={{ marginTop: "4px" }} className="search-field-error">
              <FormattedMessage id={closedHotelPropertyError} />{" "}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default withRouter(PropertiesSelect);
